// 사람 생성·이름 감지 공용. 일기에서 새 사람을 포착해 조직에 넣는 흐름(#2)과 관리 화면이 함께 쓴다.
import type { Person } from '../data/demo'

export const PALETTE: [string, string][] = [
  ['#FFB86B', '#FF6B4A'], ['#7EE0B5', '#2FA3C7'], ['#B9A6FF', '#6B5BD6'], ['#FF9EC4', '#E0548F'],
  ['#FFD36B', '#E59A1C'], ['#C4F07A', '#6CBF3A'], ['#8FD3FF', '#3F7FE0'], ['#F6A6A6', '#D9534F'],
]

export const TITLE_LV: Record<string, number> = {
  사원: 5, 인턴: 5, 주임: 4, 대리: 4, 선임: 4, 과장: 3, 차장: 3, 책임: 3, 수석: 3,
  부장: 2, 팀장: 2, 실장: 2, 파트장: 2, 본부장: 1, 이사: 1, 상무: 1, 전무: 1, 부사장: 1, 사장: 1, 대표: 1,
}
const JOBS = Object.keys(TITLE_LV).sort((a, b) => b.length - a.length)

// 이름 끝의 직함으로 직함·직급 추정(예: '이과장' → 과장/3)
export function inferTitleLevel(name: string): { title: string; level: number } {
  const suf = JOBS.find(j => name.endsWith(j) && name.length > j.length) || (TITLE_LV[name.slice(-2)] ? name.slice(-2) : '')
  return suf ? { title: suf, level: TITLE_LV[suf] } : { title: '', level: 4 }
}

// 흔한 한국 성씨(한 글자) + 복성(두 글자). 이름 후보는 성씨로 시작해야 한다(오탐 차단).
const SURNAMES = new Set((
  '김이박최정강조윤장임한오서신권황안송전홍유고문양손배백허남심노하곽성차주우구민류진지엄채원방공현함변염여추도소마길위연표명기반왕금옥육인맹제탁국어은편용'
).split(''))
const SURNAMES2 = new Set(['남궁', '선우', '황보', '제갈', '사공', '서문', '독고', '동방', '망절'])
// 존칭 앞에 자주 오지만 사람 이름이 아닌 일반명사(성씨로 시작해 성씨 검사만으론 안 걸러짐)
const STOPWORDS = new Set(['고객', '주인', '기사', '여사', '선생', '본인', '사모', '회장', '부회장', '교수', '원장', '점장'])

// 직함 단독(대표/사장/전무…)·일반명사가 아니고, 성씨로 시작하면 사람 이름으로 본다.
function looksLikeName(s: string): boolean {
  if (TITLE_LV[s]) return false           // 직함만 있는 경우(예: 전무님 → 전무)
  if (STOPWORDS.has(s)) return false       // 일반명사(예: 고객님 → 고객)
  return SURNAMES2.has(s.slice(0, 2)) || SURNAMES.has(s[0])
}

// 일기 텍스트에서 '아직 등록 안 된' 사람 후보를 뽑는다(규칙 기반, 사용자 확인 전제).
//  - 성+직함 통째: 이과장, 김부장  (앱의 이름 표기와 같은 꼴)
//  - 이름+존칭: 홍길동님 → 홍길동
const JOB_ALT = JOBS.join('|')
const HON_ALT = '님|씨|선배|후배|선생님|쌤'
const NAME_RE = new RegExp(`[가-힣]{1,2}(?:${JOB_ALT})|([가-힣]{2,4})(?:${HON_ALT})`, 'g')

export function detectNewNames(text: string, existing: { name: string; aliases?: string[] }[]): string[] {
  const known = new Set<string>()
  for (const p of existing) { if (p.name) known.add(p.name); for (const a of p.aliases || []) known.add(a) }
  const seen = new Set<string>()
  const out: string[] = []
  for (const m of text.matchAll(NAME_RE)) {
    const cand = (m[1] || m[0]).trim()
    if (!cand || cand.length < 2 || seen.has(cand)) continue
    seen.add(cand)
    if (!looksLikeName(cand)) continue     // 성씨 시작 + 직함·일반명사 아님
    // 이미 아는 사람(또는 서로 포함 관계)이면 건너뜀
    if (known.has(cand)) continue
    if ([...known].some(k => k.length >= 2 && (k.includes(cand) || cand.includes(k)))) continue
    out.push(cand)
  }
  return out
}

// 기본값으로 새 사람을 만든다(색은 순번으로). extra로 직함·부서·직급 등을 덮어쓴다.
export function makePerson(name: string, colorIdx: number, extra: Partial<Person> = {}): Person {
  const [c1, c2] = PALETTE[colorIdx % PALETTE.length]
  const inf = inferTitleLevel(name)
  return {
    id: crypto.randomUUID(), name, title: inf.title, rel: '', level: inf.level,
    dept: null, close: 0.3, c1, c2, aliases: [],
    tip: '아직 기록이 없어요. 한 줄씩 남겨 보세요.', dos: [], donts: [],
    ...extra,
  }
}
